import { useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { BrandLines } from "@/components/ui/BrandLines";
import { cn } from "@/lib/utils";
import { subscribeNewsletter, NEWSLETTER_CONSENT_TEXT } from "@/lib/newsletter.functions";

interface Props {
  variant?: "inline" | "footer";
  sourceType: "brand" | "footer" | "coupons" | "category";
  sourceSlug?: string;
  brandName?: string;
  className?: string;
  /** Sobrescreve título/descrição padrão (ex.: marca desativada). */
  title?: string;
  description?: string;
}

export function NewsletterSignup({ variant = "inline", sourceType, sourceSlug, brandName, className, title: titleProp, description: descriptionProp }: Props) {
  const submit = useServerFn(subscribeNewsletter);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [msg, setMsg] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!consent) { setStatus("error"); setMsg("Marque a caixa de aceite para continuar."); return; }
    if (name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setStatus("error"); setMsg("Confira seu nome e e-mail."); return;
    }
    setStatus("loading");
    try {
      const r = await submit({ data: { name, email, consent: true, website, sourceType, sourceSlug } });
      if (r.ok) { setStatus("done"); return; }
      setStatus("error");
      setMsg(r.error === "rate_limited" ? "Muitas tentativas. Tente novamente mais tarde." : "Não foi possível cadastrar agora. Tente novamente.");
    } catch {
      setStatus("error"); setMsg("Confira seu nome e e-mail.");
    }
  };

  const title = titleProp ?? (brandName
    ? `Receba os melhores cupons da ${brandName} e gere impacto`
    : "Cupons exclusivos e cashback solidário no seu e-mail");
  const description = descriptionProp ??
    "Compre com desconto e gere impacto ao mesmo tempo. Sem spam, saia quando quiser.";
  const isFooter = variant === "footer";
  const idp = `nl-${sourceType}`;

  return (
    <section
      aria-labelledby={`${idp}-title`}
      className={cn(
        "overflow-hidden rounded-xl border bg-card shadow-[0_14px_34px_-20px_hsl(var(--foreground)/0.3)]",
        className,
      )}
    >
      <div className={cn("p-5 sm:p-6", isFooter && "md:flex md:items-start md:gap-8")}>
        <div className={cn("mb-4", isFooter && "md:mb-0 md:w-1/3")}>
          <h2 id={`${idp}-title`} className="flex items-center gap-2 text-base sm:text-lg font-bold text-foreground">
            <Mail className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
            {title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {description}
          </p>
        </div>

        {status === "done" ? (
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground" role="status">
            <CheckCircle2 className="h-5 w-5 text-primary" aria-hidden="true" />
            Pronto, {name.split(" ")[0]}! Você vai receber nossos melhores cupons.
          </p>
        ) : (
          <form onSubmit={onSubmit} noValidate className={cn("space-y-3", isFooter && "md:flex-1")}>
            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <label className="sr-only" htmlFor={`${idp}-name`}>Nome</label>
              <Input id={`${idp}-name`} placeholder="Seu nome" value={name} maxLength={80}
                autoComplete="given-name" onChange={(e) => setName(e.target.value)} />
              <label className="sr-only" htmlFor={`${idp}-email`}>E-mail</label>
              <Input id={`${idp}-email`} type="email" placeholder="Seu melhor e-mail" value={email} maxLength={255}
                autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
              <Button type="submit" disabled={status === "loading"}>
                {status === "loading" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Quero receber
              </Button>
            </div>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
              className="hidden" value={website} onChange={(e) => setWebsite(e.target.value)} />
            <label htmlFor={`${idp}-consent`} className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <Checkbox id={`${idp}-consent`} checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
              <span>
                {NEWSLETTER_CONSENT_TEXT}{" "}
                <a href="/privacidade" className="underline hover:text-primary">Política de privacidade</a>.
              </span>
            </label>
            {status === "error" && <p className="text-xs text-destructive" role="alert">{msg}</p>}
          </form>
        )}
      </div>
      <BrandLines />
    </section>
  );
}
